import React, { useCallback, useRef, useState } from "react"
import * as S from "./map-chart.styles"
import { Map, Marker, NavigationControl, Popup } from "react-map-gl"
import Circle from "../../assets/icons/Circle.svg"
import Puerto from "../../assets/icons/puerto-rico.svg"
import Location from "../../assets/icons/pin-logo.svg"
import "mapbox-gl/dist/mapbox-gl.css"
import { getCircleColor } from "../../utils"
import MapStyle from "../../styles/map-style.json"
import CustomButton from "../../components/custom-button/custom-button.component"
import {
  IconButton,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { ArrowForward, Close } from "@mui/icons-material"
import Container from "@mui/material/Container"
import parse from "html-react-parser"
import Grid from "@mui/material/Grid"

const MapChart = ({ locations, title, hideZoomBox, activeTitle }) => {
  const theme = useTheme()
  const isMd = useMediaQuery(theme.breakpoints.up("md"))
  const isSm = useMediaQuery(theme.breakpoints.up("sm"))
  const center = isMd ? [-94.941065, 35.134663] : [-95.353346, 32.174557]
  const centerOffices = isMd ? [-88.941065, 37.134663] : [-82.353346, 32.174557]
  const mapRef = useRef(null)
  const [popupInfo, setPopupInfo] = useState()
  const [anchorEl, setAnchorEl] = useState(null)

  const card = (item, className) => (
    <S.PopupWrapper className={`${item.type} ${className}`} key={item.address1}>
      <S.InfoWrapper className={item.type}>
        <S.InfoTitle className="title">
          <Location className="location" />
          <h2>{item?.title}</h2>
          {className !== "fullwidth" && (
            <IconButton onClick={handlePopupClose}>
              <Close />
            </IconButton>
          )}
        </S.InfoTitle>
        {item?.addressTag && <Typography>{item?.addressTag}</Typography>}
        {item?.address1 && <Typography>{parse(item?.address1)}</Typography>}
        {item?.address2 && <Typography>{item?.address2}</Typography>}
        {item?.main && (
          <Typography className="main">Main: {item?.main}</Typography>
        )}
        {item?.contact && <Typography>{item?.contact}</Typography>}
        {item?.services && (
          <S.Services>
            {item?.services?.map(({ name }) => (
              <S.Tag>{name}</S.Tag>
            ))}
          </S.Services>
        )}
      </S.InfoWrapper>
      {item.type !== "pending" && (
        <S.BottomWrapper className={item.type} href={item.link} target="_blank">
          Get Directions <ArrowForward />
        </S.BottomWrapper>
      )}
    </S.PopupWrapper>
  )

  const mapRefCallback = useCallback(
    ref => {
      if (ref !== null) {
        mapRef.current = ref
        ref.flyTo({
          zoom: isMd ? 3.8 : hideZoomBox ? 2.4 : 3.6,
          center: hideZoomBox ? center : centerOffices,
          duration: 1000,
          essential: true,
        })
      }
    },
    [isMd]
  )

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }
  const handlePopupClose = () => {
    setAnchorEl(null)
    setPopupInfo(null)
  }
  const open = Boolean(anchorEl)
  const handleDotClick = item => {
    setPopupInfo(item)
    if (!isSm) {
      mapRef?.current?.flyTo({
        center: [item.long, item.lat],
        offset: [0, hideZoomBox ? 100 : 50],
        duration: 2000,
        essential: true,
      })
    }
  }
  const headquarters = locations.filter(
    ({ item }) => item.type === "headquarter"
  )
  const satellites = locations.filter(({ item }) => item.type === "active")
  const pending = locations.filter(({ item }) => item.type === "pending")
  const puertoRicoInfo = locations.find(({ item }) => item.isPuertoRico)?.item

  return (
    <>
      <S.Wrapper>
        {/*<S.TitleContainer>*/}
        {/*  <S.Title>{title}</S.Title>*/}
        {/*  {!isSm && (*/}
        {/*    <CustomButton onClick={handleClick} className="transparent">*/}
        {/*      {selectedOption === "map" ? "Show List" : "Show on map"}*/}
        {/*    </CustomButton>*/}
        {/*  )}*/}
        {/*</S.TitleContainer>*/}

        <S.MapWrapper>
          <Map
            initialViewState={{
              longitude: -92.950374,
              latitude: 35.175457,
              zoom: 3.8,
            }}
            projection="albers"
            mapStyle={MapStyle}
            mapboxAccessToken={process.env.GATSBY_MAPBOX_ACCESS_TOKEN}
            ref={mapRefCallback}
            // scrollZoom={false}
          >
            {locations
              ?.filter(({ item }) => !item?.isPuertoRico)
              ?.map(({ item }, index) => (
                <Marker
                  key={`marker-${index}`}
                  longitude={Number(item.long)}
                  latitude={Number(item.lat)}
                  anchor="center"
                >
                  <S.MarkerBox
                    className={item?.type}
                    // onMouseLeave={() => {
                    //   setPopupInfo(null)
                    // }}
                    onMouseEnter={() => {
                      handleDotClick(item)
                    }}
                    onClick={() => {
                      handleDotClick(item)
                    }}
                  >
                    <Circle
                      style={{
                        fill: getCircleColor(item.type),
                      }}
                    />
                  </S.MarkerBox>
                </Marker>
              ))}
            {popupInfo && (
              <Popup
                anchor={isSm ? popupInfo.framePosition : "bottom"}
                longitude={Number(popupInfo.long)}
                latitude={Number(popupInfo.lat)}
                closeOnClick={false}
              >
                {card(popupInfo)}
              </Popup>
            )}
            <NavigationControl position="top-right" showCompass={false} />
          </Map>

          <S.CustomBox>
            {!hideZoomBox && (
              <S.Item>
                <Circle
                  style={{
                    fill: "#F3A94C",
                  }}
                />
                Headquarter
              </S.Item>
            )}
            <S.Item>
              <Circle
                style={{
                  fill: "#013B82",
                }}
              />
              {activeTitle ?? "Active"} ({satellites?.length})
            </S.Item>
            <S.Item>
              <Circle
                style={{
                  fill: "#767676",
                }}
              />
              Coming Soon ({pending?.length})
            </S.Item>
          </S.CustomBox>
          {!hideZoomBox && puertoRicoInfo && (
            <S.CustomBox className="zoom-box">
              <S.PuertoTitle>Puerto Rico</S.PuertoTitle>
              <S.BottomContainer>
                <Puerto />
                <S.MarkerBox
                  className="zoom-box"
                  aria-owns={open ? "mouse-over-popover" : undefined}
                  aria-haspopup="true"
                  onMouseEnter={handlePopoverOpen}
                  onClick={handlePopoverOpen}
                  // onMouseLeave={handlePopoverClose}
                >
                  <Circle
                    style={{
                      fill: "#013B82",
                    }}
                  />
                </S.MarkerBox>
                <Popover
                  id="mouse-over-popover"
                  open={open}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  onClose={handlePopoverClose}
                  disableRestoreFocus
                >
                  {card({
                    ...puertoRicoInfo,
                  })}
                </Popover>
              </S.BottomContainer>
            </S.CustomBox>
          )}
        </S.MapWrapper>
      </S.Wrapper>
      <S.ListWrapper contained maxWidth="md">
        <Grid container rowSpacing={4}>
          <Grid item xs={12}>
            <S.Title>{title}</S.Title>
          </Grid>
          {headquarters?.length > 0 && (
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12}>
                <S.TypeTitle>Headquarter ({headquarters?.length})</S.TypeTitle>
              </Grid>
              {headquarters?.map(item => (
                <Grid key={item?.item.title} item xs={12}>
                  {card(item.item, "fullwidth")}
                </Grid>
              ))}
            </Grid>
          )}
          {satellites?.length > 0 && (
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12}>
                <S.TypeTitle>
                  {activeTitle ?? "Active"} ({satellites?.length})
                </S.TypeTitle>
              </Grid>
              {satellites?.map(item => (
                <Grid key={item?.item.title} item xs={12} sm={6}>
                  {card(item.item, "fullwidth")}
                </Grid>
              ))}
            </Grid>
          )}
          {pending?.length > 0 && (
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12}>
                <S.TypeTitle>Coming soon ({pending?.length})</S.TypeTitle>
              </Grid>
              {pending?.map(item => (
                <Grid key={item?.item.title} item xs={12} sm={6}>
                  {card(item.item, "fullwidth")}
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </S.ListWrapper>
    </>
  )
}
export default MapChart
